import { useMemo, useReducer, useState } from 'react'
import { toast } from '@atrvd/saas-ui'
import { sumAmounts } from '@dcp/shared'
import { useCategories, useCreateProjection, useDeleteProjection, useUpdateProjection } from '@/hooks/useProjections'
import {
  useBulkMoveProjectionsToNextDay,
  useBulkSetProjectionDate,
  useProjectionCellRows,
} from '@/hooks/useProjectionCell'
import { useTablePagination } from '@/hooks/useTablePagination'
import { tableQueryState, type TableQuerySource } from '@/components/common/queryErrorDisplay'
import { nextCalendarDay } from '@/lib/date-utils'
import { pageOfIndex } from '@/lib/pagination'
import { getDefaultPriority } from '@/lib/projection-utils'
import {
  diffDraft,
  draftFromRecord,
  cellFacilities,
  isDraftValid,
  isOutsidePeriod,
  outflowCategoryLabel,
  outflowCategoryOptions,
  resolveCellCategoryIds,
  sortCellRows,
  validateDraft,
  duplicatePayload,
  type ProjectionCellScope,
  type ProjectionDraft,
  type ProjectionRecord,
} from '@/lib/projection-cell'
import {
  initialManageProjectionsState,
  manageProjectionsReducer,
  nextNewRowKey,
  tableRows,
  unsavedCount,
  visibleSelection,
} from './manageProjectionsState'
import {
  SELECT_ALL_CHECKBOX_ID,
  isFocusInBulkBar,
  isPopperOpen,
  rowControlId,
  useFocusRequest,
  type RowControl,
} from './manageProjectionsFocus'
import {
  ADDED_OUTSIDE_PERIOD,
  MOVED_OUT_OF_PERIOD,
  bulkNextDayNotice,
  bulkSetDateNotice,
  rowNextDayNotice,
  type Notice,
} from './manageProjectionsNotices'

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

function showNotice(notice: Notice): void {
  const options = notice.description ? { description: notice.description } : undefined
  if (notice.tone === 'success') toast.success(notice.title, options)
  else toast.error(notice.title, options)
}

/**
 * State and actions of the "Manage projections" modal for one cell. Every write
 * waits for the cell's own refetch before leaving the busy state, so a saved row
 * never flashes its old values. `onClose` is called only when nothing unsaved
 * would be lost.
 */
export function useManageProjections(scope: ProjectionCellScope, onClose: () => void) {
  const [state, dispatch] = useReducer(manageProjectionsReducer, initialManageProjectionsState)

  const categoriesQuery = useCategories()
  const categories = categoriesQuery.data
  const categoryIds = useMemo(
    () => (categories ? resolveCellCategoryIds(categories, scope.row) : null),
    [categories, scope.row],
  )
  const cell = useProjectionCellRows(scope, categoryIds)
  const { settle } = cell

  const createProjection = useCreateProjection()
  const updateProjection = useUpdateProjection()
  const deleteProjection = useDeleteProjection()
  const bulkSetDate = useBulkSetProjectionDate()
  const bulkMoveToNextDay = useBulkMoveProjectionsToNextDay()
  // Its own instance, so a single row's move does not read as a bulk write in flight.
  const rowMoveToNextDay = useBulkMoveProjectionsToNextDay()

  const rows = useMemo(() => sortCellRows(cell.rows), [cell.rows])
  const rowIds = useMemo(() => rows.map((row) => row.id), [rows])
  const rowIdSet = useMemo(() => new Set(rowIds), [rowIds])
  // Count and Total cover every row of the cell. Selection, like every other
  // table's, covers the visible page only: select all ticks the page, and a
  // page or page size change drops the ticks, so a bulk write never reaches
  // rows the user cannot see.
  const pagination = useTablePagination({ totalItems: rows.length })
  const pageRows = pagination.paginate(rows)
  const pageRowIds = pageRows.map((row) => row.id)
  const pageKey = `${pagination.page}:${pagination.pageSize}`
  const [selectionPage, setSelectionPage] = useState(pageKey)
  if (selectionPage !== pageKey) {
    setSelectionPage(pageKey)
    dispatch({ type: 'setSelected', ids: [] })
  }
  const selectedIds = visibleSelection(state, pageRowIds)
  const unsaved = unsavedCount(state, rowIdSet)
  const total = sumAmounts(rows.map((row) => row.amount))

  const categoryNameById = useMemo(
    () => new Map((categories ?? []).map((c) => [c.id, c.name])),
    [categories],
  )
  const outflowOptions = useMemo(() => outflowCategoryOptions(categories ?? []), [categories])
  const categoryLabel = (categoryId: string): string => outflowCategoryLabel(categoryNameById.get(categoryId))

  const facilityOptions = useMemo(() => cellFacilities(scope.target), [scope.target])
  const facilityNameById = useMemo(
    () => new Map(facilityOptions.map((facility) => [facility.id, facility.name])),
    [facilityOptions],
  )
  const facilityLabel = (facilityId: string): string => facilityNameById.get(facilityId) ?? ''

  const requestFocus = useFocusRequest()

  /**
   * Opens the page holding the first of `ids` in the refetched cell, so a
   * written row that re-sorted onto another page stays in view (a bulk write:
   * the first of its rows, in table order). Nothing when they all left the cell.
   */
  const showRows = (freshRows: ProjectionRecord[], ids: readonly string[]) => {
    const written = new Set(ids)
    const index = sortCellRows(freshRows).findIndex((row) => written.has(row.id))
    if (index >= 0) pagination.setPage(pageOfIndex(index, pagination.pageSize))
  }

  const addRow = () => {
    requestFocus(rowControlId(nextNewRowKey(state), 'date'))
    dispatch({
      type: 'addRow',
      draft: {
        date: '',
        // A facility cell presets its facility; a pack cell asks which member.
        facilityId: scope.target.kind === 'facility' ? scope.target.id : '',
        // A single-category cell presets its category; Projected Outflows asks.
        categoryId: scope.row.kind === 'category' ? (categoryIds?.[0] ?? '') : '',
        description: '',
        amount: undefined,
      },
    })
  }

  const startEdit = (record: ProjectionRecord) => {
    requestFocus(rowControlId(record.id, 'date'))
    dispatch({ type: 'startEdit', id: record.id, base: draftFromRecord(record) })
  }

  const changeDraft = (key: string, patch: Partial<ProjectionDraft>) => {
    dispatch({ type: 'changeDraft', key, patch })
  }

  const discardRow = (key: string) => {
    // A new row has no Edit button to return to; the Add button catches it.
    requestFocus(rowControlId(key, 'edit'))
    dispatch({ type: 'discardRow', key })
  }

  /** Saves an edited row as a diff, a new row as a create. */
  const applyRow = async (key: string) => {
    const edit = state.edits[key]
    const draft = edit?.draft ?? state.newRows.find((row) => row.key === key)?.draft
    if (!draft || state.saving[key]) return
    const errors = validateDraft(draft)
    dispatch({ type: 'setErrors', key, errors })
    if (!isDraftValid(errors)) return

    const patch = edit ? diffDraft(edit.base, draft) : null
    if (patch && Object.keys(patch).length === 0) {
      discardRow(key)
      return
    }

    dispatch({ type: 'writeStarted', key })
    try {
      // `amount` is defined once the draft validated; the sign is stored as typed.
      const saved = patch
        ? await updateProjection.mutateAsync({ id: key, data: patch })
        : await createProjection.mutateAsync({
            facility_id: draft.facilityId,
            category_id: draft.categoryId,
            date: draft.date,
            description: draft.description.trim(),
            amount: draft.amount,
            include_in_report: true,
            priority: getDefaultPriority(categories ?? [], draft.categoryId),
          })
      showRows(await settle(), [saved.id])
      // The saved row's Edit button, or — when it left the cell — the Add button.
      requestFocus(rowControlId(saved.id, 'edit'))
      dispatch({ type: 'writeFinished', key, leaveEditMode: true })
      if (!isOutsidePeriod(saved.date, scope)) toast.success(patch ? 'Projection updated' : 'Projection added')
      else if (patch) toast.success(MOVED_OUT_OF_PERIOD)
      else toast.success('Projection added', { description: ADDED_OUTSIDE_PERIOD })
    } catch (error) {
      requestFocus(rowControlId(key, 'apply'))
      dispatch({ type: 'writeFinished', key, leaveEditMode: false })
      toast.error(errorMessage(error, patch ? 'Failed to update projection' : 'Failed to add projection'))
    }
  }

  /**
   * A one-row write without a draft (copy, next day): the table follows the
   * written row (`shownId`) to its page, and focus returns to `control` of `key`.
   */
  const runRowWrite = async (
    key: string,
    control: RowControl,
    write: () => Promise<{ notice: Notice; shownId: string }>,
    fallback: string,
  ) => {
    if (state.saving[key]) return
    dispatch({ type: 'writeStarted', key })
    try {
      const { notice, shownId } = await write()
      showRows(await settle(), [shownId])
      showNotice(notice)
    } catch (error) {
      toast.error(errorMessage(error, fallback))
    } finally {
      requestFocus(rowControlId(key, control))
      dispatch({ type: 'writeFinished', key, leaveEditMode: false })
    }
  }

  const duplicateRow = (record: ProjectionRecord) =>
    runRowWrite(
      record.id,
      'copy',
      async () => {
        const copy = await createProjection.mutateAsync(duplicatePayload(record))
        return { notice: { tone: 'success', title: 'Projection copied' }, shownId: copy.id }
      },
      'Failed to copy projection',
    )

  const moveRowToNextDay = (record: ProjectionRecord) =>
    runRowWrite(
      record.id,
      'next-day',
      // The bulk move's date-guarded write, so a row re-dated elsewhere is not moved.
      async () => ({
        notice: rowNextDayNotice(
          await rowMoveToNextDay.mutateAsync([record]),
          isOutsidePeriod(nextCalendarDay(record.date), scope),
        ),
        shownId: record.id,
      }),
      'Failed to move projection',
    )

  const requestDelete = (record: ProjectionRecord) => dispatch({ type: 'requestDelete', id: record.id })
  const cancelDelete = () => dispatch({ type: 'closeDelete' })

  const confirmDelete = async () => {
    const id = state.pendingDeleteId
    if (!id) return
    dispatch({ type: 'writeStarted', key: id })
    try {
      await deleteProjection.mutateAsync(id)
      await settle()
      dispatch({ type: 'rowRemoved', id })
      toast.success('Projection deleted')
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to delete projection'))
    } finally {
      // Back to the row's Delete button after a failure; the row is gone after a success.
      requestFocus(rowControlId(id, 'delete'))
      dispatch({ type: 'writeFinished', key: id, leaveEditMode: false })
      // Only this row's confirmation: another one opened meanwhile stays.
      dispatch({ type: 'deleteFinished', id })
    }
  }

  /** The table's selection change: a row tick, select all, or the bulk bar's Clear selection. */
  const changeSelection = (keys: ReadonlySet<string | number>) => {
    // Clear selection unmounts the bulk bar under the focus; select-all catches it.
    if (keys.size === 0 && isFocusInBulkBar()) requestFocus(SELECT_ALL_CHECKBOX_ID)
    dispatch({ type: 'setSelected', ids: Array.from(keys, String) })
  }
  // After a bulk write: the bulk bar unmounts, so focus moves to select-all.
  const clearSelection = () => {
    requestFocus(SELECT_ALL_CHECKBOX_ID)
    dispatch({ type: 'setSelected', ids: [] })
  }

  const bulkBusy = bulkSetDate.isPending || bulkMoveToNextDay.isPending

  const applyBulkDate = async (date: string) => {
    if (selectedIds.length === 0 || bulkBusy) return
    try {
      const outcome = await bulkSetDate.mutateAsync({ ids: selectedIds, date })
      showRows(await settle(), selectedIds)
      clearSelection()
      showNotice(bulkSetDateNotice(outcome, isOutsidePeriod(date, scope)))
    } catch (error) {
      requestFocus(SELECT_ALL_CHECKBOX_ID)
      toast.error(errorMessage(error, 'Failed to update dates'))
    }
  }

  const bulkNextDay = async () => {
    if (selectedIds.length === 0 || bulkBusy) return
    const selected = rows.filter((row) => selectedIds.includes(row.id))
    const leaving = selected.filter((row) => isOutsidePeriod(nextCalendarDay(row.date), scope)).length
    try {
      const outcome = await bulkMoveToNextDay.mutateAsync(selected)
      showRows(await settle(), selectedIds)
      clearSelection()
      showNotice(bulkNextDayNotice(outcome, leaving))
    } catch (error) {
      requestFocus(SELECT_ALL_CHECKBOX_ID)
      toast.error(errorMessage(error, 'Failed to move projections'))
    }
  }

  // Derived from the listed rows: a row that vanished meanwhile closes its confirmation.
  const pendingDelete = state.pendingDeleteId ? rows.find((row) => row.id === state.pendingDeleteId) : undefined

  /** Ignored while a confirmation or popper is open: that same Escape is closing it. */
  const requestClose = () => {
    if (pendingDelete || state.confirmDiscard || isPopperOpen()) return
    if (unsaved > 0) dispatch({ type: 'requestDiscardAll' })
    else onClose()
  }

  const keepEditing = () => dispatch({ type: 'closeDiscardAll' })

  const isRowBusy = (id: string) => state.saving[id] === true || (bulkBusy && selectedIds.includes(id))

  // Retries only the failed query: the cell's query waits for the categories.
  const retryLoad = () => {
    if (categoriesQuery.error) void categoriesQuery.refetch()
    if (cell.error) void cell.refetch()
  }
  // The table depends on both queries; seen as one source, a failed refetch
  // (e.g. the settle after a write) keeps the listed rows (queryErrorDisplay).
  const loadSource: TableQuerySource = {
    isPending: categoriesQuery.isPending || cell.isPending,
    isPlaceholderData: false,
    isFetching: categoriesQuery.isFetching || cell.isFetching,
    error: categoriesQuery.error ?? cell.error,
    refetch: retryLoad,
  }
  const listedCount = rows.length + state.newRows.length

  return {
    state,
    /** Every row of the cell, in table order. */
    rows,
    /** The table's rows: new rows, then the current page's saved rows. */
    tableRows: tableRows(state, pageRows),
    /** The DataTable `pagination` prop; `undefined` when the footer is hidden. */
    pagination: pagination.dataTablePagination,
    total,
    unsaved,
    selectedIds,
    outflowOptions,
    categoryNameById,
    categoryLabel,
    /** The cell's facilities: the Facility picker's choices in a pack cell. */
    facilityOptions,
    /** Name of one of the cell's facilities, `''` for any other id. */
    facilityLabel,
    /** Loading and error state of the table's two queries, as one. */
    load: tableQueryState(loadSource, listedCount),
    /** Rows the table lists across pages: saved plus new — what decides banner vs in-table error. */
    listedCount,
    /** Both queries have answered once: the rows, count and total are real (maybe stale after a failed refetch). */
    loaded: categoriesQuery.data !== undefined && cell.data !== undefined,
    bulkBusy,
    // Per row: a delete still running for another row must not spin this one.
    deletePending: state.pendingDeleteId !== null && state.saving[state.pendingDeleteId] === true,
    isRowBusy,
    pendingDelete,
    addRow,
    startEdit,
    changeDraft,
    discardRow,
    applyRow,
    duplicateRow,
    moveRowToNextDay,
    requestDelete,
    cancelDelete,
    confirmDelete,
    changeSelection,
    applyBulkDate,
    bulkNextDay,
    requestClose,
    keepEditing,
  }
}
